import React from "react";
import { formatKey, formatValue, formatCurrency, formatDate, formatDateTime } from "../utils/responseParser";

/**
 * DataTable - Renders generic data in a table format
 * @param {Object} props
 * @param {Object} props.data - The data object to display
 * @param {string} props.title - Optional title for the table
 * @returns {JSX.Element}
 */
const DataTable = ({ data, title }) => {
    if (!data || typeof data !== "object" || Object.keys(data).length === 0) return null;

    /**
     * Smart value formatter that detects currency and date values
     */
    const smartFormatValue = (key, value) => {
        if (value === null || value === undefined) return "-";

        // Check for currency values by key name
        const keyLower = key.toLowerCase();
        if (keyLower.includes("amount") || keyLower.includes("price") || keyLower.includes("cost") || keyLower.includes("charge") || keyLower.includes("total")) {
            if (typeof value === "number") {
                return formatCurrency(value);
            }
        }

        // Check for date values by key name
        if (keyLower.includes("date") || keyLower.includes("time") || keyLower.includes("at")) {
            if (typeof value === "string" || value instanceof Date) {
                const formatted = keyLower.includes("time") || keyLower === "at" ? formatDateTime(value) : formatDate(value);
                if (formatted !== "-") return formatted;
            }
        }

        // Default formatting
        return formatValue(value);
    };

    /**
     * Check if a value should be displayed (filter internal/private fields)
     */
    const shouldDisplayField = (key) => {
        const excludeKeys = ["id", "_id", "internalid", "internal_id", "createdat", "created_at", "updatedat", "updated_at"];
        const keyLower = key.toLowerCase();
        return !excludeKeys.some(exclude => keyLower === exclude);
    };

    const entries = Object.entries(data).filter(([key]) => shouldDisplayField(key));

    if (entries.length === 0) return null;

    return (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden">
            {title && (
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
                </div>
            )}
            <table className="w-full text-xs">
                <tbody>
                    {entries.map(([key, value]) => (
                        <tr key={key} className="border-b border-gray-100 last:border-b-0">
                            <td className="px-3 py-2 text-gray-500 font-medium w-1/2 align-top">
                                {formatKey(key)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 font-medium break-words align-top">
                                {smartFormatValue(key, value)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DataTable;
